import { useState } from 'react'
import useSWR, { SWRConfiguration } from 'swr'

type TUseSwrLazyFetchProps = {
  cachingKey: string
  method?: string
  headers?: HeadersInit | undefined
  body?: any
  swrOptions?: SWRConfiguration
}

const defaultSwrOptions: SWRConfiguration = {
  revalidateOnFocus: false,
}

const useSwrLazyFetch = <TData = any, TError = any>({
  cachingKey,
  method = 'get',
  headers,
  body,
  swrOptions = defaultSwrOptions,
}: TUseSwrLazyFetchProps) => {
  const [isLazyLoading, setIsLazyLoading] = useState<boolean>(false)
  const [lazyLoadingError, setLazyLoadingError] = useState<
    TError | string | undefined
  >(undefined)

  const results = useSWR<TData | undefined, TError | string>(
    cachingKey,
    () => undefined,
    swrOptions
  )

  const executeRequest = async (url: string) => {
    setIsLazyLoading(true)

    const response = await fetch(url, { method, headers, body })
    const text = await response.text()

    try {
      const json = JSON.parse(text)

      if (response.status >= 200 && response.status <= 299)
        results.mutate(json, false)
      else {
        results.mutate(undefined, false)
        setLazyLoadingError(json)
      }
    } catch {
      results.mutate(undefined, false)
      setLazyLoadingError(text)
    } finally {
      setIsLazyLoading(false)
    }
  }

  return [
    executeRequest,
    {
      ...results,
      isLoading: results.isLoading || isLazyLoading,
      error: results.error || lazyLoadingError,
    },
  ] as const
}

export { useSwrLazyFetch }
export type { TUseSwrLazyFetchProps }
